import { ChangeEventHandler, FormEventHandler, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { EMAIL_MAX_LENGTH } from "../consts/constants";
import { MYPAGE_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { updateUser, getUsers } from "../clients/UserClient";
import { getStorageSizeOthers } from "../clients/S3ApiClient";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { User } from "../types/User";
import i18n from "../i18n/configs";
import { OnlyMasterUserAuthorized } from "../components/OnlyMasterUserAuthorized";
import { Helmet } from "react-helmet";

type UserWithStorageUsage = User & { storage_usage_size: number };

/**
 * Update storage size 利用者検索画面
 * @returns JSX.Element
 */
const StorageIssueUserSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getRecentToken } = useUserContext();
    const [userList, setUserList] = useState<UserWithStorageUsage[]>([]);
    const [email, setEmail] = useState<string>("");
    const [selectedEmail, setSelectedEmail] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [changedUserStorageMaxSizeGB, setChangedUserStorageMaxSizeGB] = useState<number>(0);

    /**
     * 処理中フラグ
     *
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isProcessingToChangeStorageSize, setIsProcessingToChangeStorageSize] = useState<boolean>(false);

    /**
     * メールアドレス入力時のイベント処理
     * @param event
     */
    const handleEmailChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        setEmail(event.target.value);
    };

    /**
     * 検索押下時のイベント処理
     * @param event
     */
    const handleSearchUser: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        if (isProcessing) return;
        setIsProcessing(true);
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]ユーザー情報一覧の取得
            const userListResponse: any = await getUsers(idToken, { email: email });
            if (userListResponse.length === 0) {
                // ユーザーが存在しない場合
                setSelectedEmail("");
                setUserList([]);
                return;
            }
            // [API]ユーザーのストレージ使用量確認
            const responseOfGetStorageSizeOthers = await getStorageSizeOthers(idToken, email);
            const usageGB = convertByteToGB(responseOfGetStorageSizeOthers.used_size);
            const addStorageSizeUsedToResponse = userListResponse.map((user: User) => ({
                ...user,
                storage_usage_size: usageGB ?? 0,
            })) as (User & { storage_usage_size: number })[];
            setSelectedEmail(email);
            setUserList(addStorageSizeUsedToResponse);
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.failToFetchInfo"));
            return;
        } finally {
            setIsProcessing(false);
        }
    };

    /**
     * 変更押下時のイベント処理
     * @param event
     */
    const handleStorageChange: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        if (isProcessingToChangeStorageSize) return;
        setIsProcessingToChangeStorageSize(true);
        try {
            const targetUser = userList[0];
            if (!targetUser) {
                // ユーザー情報が存在しないためスキップ（想定しない）
                return;
            }
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API] ユーザー情報更新
            await updateUser(idToken, targetUser.user_id, { storage_size: changedUserStorageMaxSizeGB }, false);
            // [API] ユーザー情報の再取得
            const response: any = await getUsers(idToken, { email: selectedEmail });
            const responseOfGetStorageSizeOthers = await getStorageSizeOthers(idToken, selectedEmail);
            const usageGB = convertByteToGB(responseOfGetStorageSizeOthers.used_size);
            const addStorageSizeUsedToResponse = response.map((user: User) => ({
                ...user,
                storage_usage_size: usageGB ?? 0,
            })) as (User & { storage_usage_size: number })[];
            setUserList(addStorageSizeUsedToResponse);
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
        } finally {
            setIsProcessingToChangeStorageSize(false);
        }
    };

    /**
     * ストレージサイズ値選択時のonChangeイベント処理
     * @param event
     */
    const handleStorageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(e.target.value, 10); // 入力値を整数に変換
        // 数値でない場合は 1 に設定
        if (isNaN(value)) {
            value = 1;
        } else if (value < 1) {
            value = 1; // 1GB 未満は 1 に強制
        } else if (value > 999) {
            value = 999; // 999GB を超える場合は 999 に強制
        }
        e.target.value = String(value); // 入力フィールドを修正
        setChangedUserStorageMaxSizeGB(value); // ステート更新
    };

    /**
     * 戻る押下時のイベント処理 CHANGE
     */
    const handleBack = () => {
        navigate(MYPAGE_ROUTE);
    };

    /** GB文字列に変換 */
    function convertByteToGB(number: number): string {
        return (number / 1024 / 1024 / 1024).toFixed(1);
    }

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title={t("StorageIssueUserSearch.meta.title")} />
            <ScrollToTop />
            {/* マスターユーザーのみ閲覧可 */}
            <OnlyMasterUserAuthorized />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">{t("StorageIssueUserSearch.title")}</h1>
                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pt-4">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}
                <form className="mx-auto max-w-lg" onSubmit={handleSearchUser}>
                    <div className="grid grid-cols-3 items-center justify-center gap-4 pb-2 pt-2 sm:m-2">
                        {/* メールアドレス */}
                        <div className="col-span-1 py-2">
                            <label htmlFor="email" className="block font-medium text-gray-900">
                                {t("CommonLabel.email")}
                            </label>
                        </div>
                        <div className="col-span-2 py-2">
                            <input
                                type="email"
                                name="email"
                                id="email"
                                placeholder="name@example.com"
                                className="textbox-common"
                                onChange={handleEmailChange}
                                maxLength={EMAIL_MAX_LENGTH}
                                required
                            />
                        </div>
                    </div>
                    {/* 検索ボタン */}
                    <div className="flex items-center justify-evenly">
                        <button
                            type="submit"
                            className={`${
                                isProcessing || isProcessingToChangeStorageSize ? "btn-primary-disabled" : "btn-primary"
                            } w-1/3 py-2`}
                            disabled={isProcessing || isProcessingToChangeStorageSize}
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center">
                                    <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                    <label className="">Processing...</label>
                                </div>
                            ) : (
                                t("CommonLabel.search")
                            )}
                        </button>
                    </div>
                </form>
                <hr className="my-5 border-b border-t-2" />
                {userList.length === 0 ? (
                    // ユーザーが存在しない場合
                    <div className="flex items-center justify-center py-4 text-lg font-bold text-gray-700">
                        {t("StorageIssueUserSearch.userNotFound")}
                    </div>
                ) : (
                    // ユーザーが見つかった場合
                    <div className="mx-auto max-w-[1000px] overflow-x-auto transition duration-500 ease-in-out">
                        <div className="flex items-center justify-center text-lg font-bold text-gray-700">
                            {t("StorageIssueUserSearch.issueToUser")}
                        </div>
                        <table className="my-3 min-w-full divide-y divide-gray-200 border">
                            <thead className="bg-gray-400">
                                <tr
                                    className={`${
                                        i18n.language === "ja" ? "text-base" : "text-sm"
                                    } font-semibold uppercase tracking-wide text-gray-100`}
                                >
                                    {/* [ヘッダ] メールアドレス */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.email")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 氏 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.familyName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 名 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.givenName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 所属 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.organization")}</span>
                                        </div>
                                    </th>
                                    {/* ストレージ */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.storage_size")}</span>
                                        </div>
                                    </th>
                                    {/* ストレージ使用量 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.storage_usage_size")}</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {userList.map((u) => (
                                    <tr key={u.user_id} className="text-sm font-semibold text-gray-800">
                                        {/* メールアドレス */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.email}</span>
                                            </div>
                                        </td>
                                        {/* 氏 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.family_name}</span>
                                            </div>
                                        </td>
                                        {/* 名 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.given_name}</span>
                                            </div>
                                        </td>
                                        {/* 所属 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.organization}</span>
                                            </div>
                                        </td>
                                        {/* ストレージ */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.storage_size} GB</span>
                                            </div>
                                        </td>
                                        {/* ストレージ使用量 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.storage_usage_size} GB</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <form className="mx-auto flex max-w-lg items-center space-x-4" onSubmit={handleStorageChange}>
                            {/* ラベル */}
                            <label htmlFor="storage" className="font-medium text-gray-900">
                                {t("StorageIssueUserSearch.changeStorageSize")}
                            </label>
                            {/* ストレージサイズ入力 */}
                            <input
                                type="number"
                                name="storage"
                                id="storage"
                                placeholder=""
                                className="textbox-common w-20 text-center"
                                onChange={handleStorageSizeChange}
                                max={999}
                                min={1}
                                step={1}
                                required
                            />
                            <span>GB</span>
                            {/* 変更ボタン */}
                            <button
                                type="submit"
                                className={`${
                                    isProcessingToChangeStorageSize ? "btn-primary-disabled" : "btn-primary"
                                } px-4 py-2`}
                                disabled={isProcessing || isProcessingToChangeStorageSize}
                            >
                                {isProcessingToChangeStorageSize ? (
                                    <div className="flex items-center justify-center">
                                        <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                        <label className="">Processing...</label>
                                    </div>
                                ) : (
                                    t("CommonLabel.change")
                                )}
                            </button>
                        </form>
                        <hr className="my-5 border-b border-t-2" />
                    </div>
                )}
                {/* 戻る/次へボタン */}
                <div className="mx-auto flex max-w-xl items-center justify-evenly py-5 ">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="btn-secondary mx-2 w-[100px]"
                        disabled={isProcessing || isProcessingToChangeStorageSize}
                    >
                        {t("CommonLabel.back")}
                    </button>
                </div>
            </div>
            <Footer />
        </div>
    );
};
export default StorageIssueUserSearch;
